import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Modal, 
  Form, 
  Alert, 
  Spinner, 
  Badge,
  Pagination,
  InputGroup,
  Row,
  Col,
  Collapse,
  DropdownButton,
  Dropdown
} from 'react-bootstrap';
import { usersAPI } from '../services/api.js';

const UsersTab = () => {
  // Состояния
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [expandedUser, setExpandedUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);

  // Настройки пагинации с сохранением в localStorage
  const [usersPerPage, setUsersPerPage] = useState(() => {
    try {
      const saved = localStorage.getItem('users_per_page');
      return saved ? parseInt(saved) : 20;
    } catch {
      return 20;
    }
  });

  // Сохранение настроек
  useEffect(() => {
    localStorage.setItem('users_per_page', usersPerPage.toString());
  }, [usersPerPage]);

  // Загрузка пользователей при изменении страницы, лимита или поиска
  useEffect(() => {
    loadUsers();
  }, [currentPage, usersPerPage]);

  // Отдельный эффект для поиска с дебаунсом
  useEffect(() => {
    if (searchTerm.trim() !== '') {
      const timer = setTimeout(() => {
        handleSearch();
      }, 500);
      
      return () => clearTimeout(timer);
    } else if (searchTerm === '') {
      setCurrentPage(1);
      loadUsers();
    }
  }, [searchTerm]);

  // Функция для получения отображаемого имени пользователя
  const getDisplayName = (user) => {
    if (!user) return 'Неизвестный';
    
    // Проверяем наличие Telegram first_name и last_name
    if (user.telegram_first_name && user.telegram_last_name) {
      return `${user.telegram_first_name} ${user.telegram_last_name}`;
    }
    if (user.telegram_first_name) {
      return user.telegram_first_name;
    }
    if (user.telegram_last_name) {
      return user.telegram_last_name;
    }
    
    // Проверяем поле name
    if (user.name) {
      // Если name похоже на username (без пробелов, только латиница и цифры)
      const isUsername = !user.name.includes(' ') && /^[a-zA-Z0-9_]+$/.test(user.name);
      if (!isUsername) {
        return user.name; // Это полное имя
      }
    }
    
    // Возвращаем username если ничего другого нет
    return user.telegram_username || user.name || 'Пользователь';
  };

  // Загрузка пользователей
  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log(`📋 Загрузка пользователей: skip=${(currentPage - 1) * usersPerPage}, limit=${usersPerPage}`);
      
      const params = {
        skip: (currentPage - 1) * usersPerPage,
        limit: usersPerPage
      };
      
      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      
      const response = await usersAPI.getUsers(params);
      
      console.log('📊 Ответ от API:', response);
      
      let usersList = [];
      let totalCount = 0;
      
      if (response && response.users && Array.isArray(response.users)) {
        usersList = response.users;
        totalCount = response.total || response.total_count || 0;
      }
      else if (response && response.data && Array.isArray(response.data)) {
        usersList = response.data;
        totalCount = response.total || response.total_count || 0;
      }
      else if (Array.isArray(response)) {
        usersList = response;
        totalCount = response.length;
      }
      else {
        console.warn('⚠️ Неизвестный формат ответа от API:', response);
        usersList = [];
        totalCount = 0;
      }
      
      const usersWithDefaults = usersList.map(user => ({
        ...user,
        telegram_first_name: user.telegram_first_name || null,
        telegram_last_name: user.telegram_last_name || null,
        telegram_username: user.telegram_username || null,
        telegram_id: user.telegram_id || null,
        name: user.name || 'Пользователь',
        email: user.email || null,
        is_active: user.is_active !== undefined ? user.is_active : true,
        role: user.role || 'user',
        created_at: user.created_at || new Date().toISOString(),
        // Добавляем displayName для удобства отображения
        displayName: getDisplayName(user)
      }));
      
      setUsers(usersWithDefaults);
      setTotalUsers(totalCount);
      setTotalPages(Math.ceil(totalCount / usersPerPage));
      
      console.log(`📈 Загружено: ${usersWithDefaults.length} пользователей, всего: ${totalCount}, страниц: ${Math.ceil(totalCount / usersPerPage)}`);
      
    } catch (err) {
      console.error('❌ Ошибка загрузки пользователей:', err);
      setError(`Не удалось загрузить список пользователей: ${err.message || 'Ошибка сети'}`);
      setUsers([]);
      setTotalUsers(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  // Обработка поиска
  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      loadUsers();
      return;
    }
    
    try {
      setSearchLoading(true);
      setCurrentPage(1);
      
      const response = await usersAPI.getUsers({
        skip: 0,
        limit: usersPerPage,
        search: searchTerm.trim()
      });
      
      console.log('🔍 Результаты поиска:', response);
      
      let usersList = [];
      let totalCount = 0;
      
      if (response && response.users && Array.isArray(response.users)) {
        usersList = response.users;
        totalCount = response.total || response.total_count || 0;
      } else if (response && response.data && Array.isArray(response.data)) {
        usersList = response.data;
        totalCount = response.total || response.total_count || 0;
      } else if (Array.isArray(response)) {
        usersList = response;
        totalCount = response.length;
      }
      
      const usersWithDefaults = usersList.map(user => ({
        ...user,
        telegram_first_name: user.telegram_first_name || null,
        telegram_last_name: user.telegram_last_name || null,
        telegram_username: user.telegram_username || null,
        telegram_id: user.telegram_id || null,
        name: user.name || 'Пользователь',
        email: user.email || null,
        is_active: user.is_active !== undefined ? user.is_active : true,
        role: user.role || 'user',
        created_at: user.created_at || new Date().toISOString(),
        displayName: getDisplayName(user)
      }));
      
      setUsers(usersWithDefaults);
      setTotalUsers(totalCount);
      setTotalPages(Math.ceil(totalCount / usersPerPage));
      
    } catch (err) {
      console.error('❌ Ошибка поиска:', err);
      setError('Ошибка при поиске пользователей');
    } finally {
      setSearchLoading(false);
    }
  };

  // Переключение раскрытия пользователя
  const toggleExpand = (userId) => {
    if (expandedUser === userId) {
      setExpandedUser(null);
      setEditingUser(null);
    } else {
      setExpandedUser(userId);
      setEditingUser(null);
    }
  };

  // Начало редактирования пользователя
  const startEdit = (user, e) => {
    e.stopPropagation();
    setEditingUser({ ...user });
    if (expandedUser !== user.id) {
      setExpandedUser(user.id);
    }
  };

  // Отмена редактирования
  const cancelEdit = (e) => {
    e?.stopPropagation();
    setEditingUser(null);
  };

  // Сохранение изменений пользователя
  const handleSave = async (e) => {
    e?.stopPropagation();
    if (!editingUser) return;

    try {
      setSaving(true);
      setError('');

      await usersAPI.updateUser(editingUser.id, {
        name: editingUser.name,
        email: editingUser.email,
        is_active: editingUser.is_active,
        role: editingUser.role
      });

      // Обновляем пользователя в списке
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === editingUser.id ? {
            ...editingUser,
            displayName: getDisplayName(editingUser)
          } : user
        )
      );

      setEditingUser(null);
      
      console.log('✅ Пользователь успешно обновлен');
      
    } catch (err) {
      console.error('❌ Ошибка обновления пользователя:', err);
      setError('Не удалось обновить пользователя: ' + (err.message || 'Ошибка сервера'));
    } finally {
      setSaving(false);
    }
  };

  // Изменение поля в форме редактирования
  const handleInputChange = (field, value) => {
    setEditingUser(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Переключение активности пользователя
  const handleToggleActive = async (userId, currentStatus, e) => {
    e.stopPropagation();
    
    try {
      await usersAPI.updateUser(userId, {
        is_active: !currentStatus
      });

      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId ? { 
            ...user, 
            is_active: !currentStatus,
            displayName: getDisplayName({ ...user, is_active: !currentStatus })
          } : user
        )
      );
      
      console.log(`✅ Статус пользователя ${userId} изменен на ${!currentStatus ? 'активен' : 'неактивен'}`);
      
    } catch (err) {
      console.error('❌ Ошибка изменения статуса пользователя:', err);
      setError('Не удалось изменить статус пользователя: ' + (err.message || 'Ошибка сервера'));
    }
  };

  // Клик на удаление пользователя
  const handleDeleteClick = (user, e) => {
    e.stopPropagation();
    setDeleteConfirm(user);
  };

  // Подтверждение удаления пользователя
  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;

    try {
      setDeleting(true);
      
      await usersAPI.deleteUser(deleteConfirm.id);

      setUsers(prevUsers => 
        prevUsers.filter(user => user.id !== deleteConfirm.id)
      );
      
      setTotalUsers(prev => Math.max(0, prev - 1));
      
      const newTotalPages = Math.ceil((totalUsers - 1) / usersPerPage);
      setTotalPages(newTotalPages);
      
      if (users.length === 1 && currentPage > 1) {
        setCurrentPage(prev => prev - 1);
      } else {
        loadUsers();
      }
      
      setDeleteConfirm(null);
      setExpandedUser(null);
      
      console.log(`✅ Пользователь ${deleteConfirm.id} успешно удален`);
      
    } catch (err) {
      console.error('❌ Ошибка удаления пользователя:', err);
      setError('Не удалось удалить пользователя: ' + (err.message || 'Ошибка сервера'));
    } finally {
      setDeleting(false);
    }
  };

  // Отмена удаления
  const handleDeleteCancel = () => {
    setDeleteConfirm(null);
  };

  // Получение полного имени пользователя (для обратной совместимости)
  const getFullName = (user) => {
    return getDisplayName(user);
  };

  // Получение инициалов
  const getInitials = (user) => {
    if (!user) return 'U';
    
    const displayName = getDisplayName(user);
    
    // Если displayName - это username (без пробелов)
    if (!displayName.includes(' ') && displayName !== 'Пользователь') {
      return displayName.charAt(0).toUpperCase();
    }
    
    return displayName
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Получение username
  const getUsername = (user) => {
    if (!user) return '';
    return user.telegram_username || user.name || 'user';
  };

  // Цвет бейджа для роли
  const getRoleBadgeVariant = (role) => {
    switch (role) {
      case 'admin': return 'danger';
      case 'moderator': return 'warning';
      case 'superuser': return 'warning';
      default: return 'secondary';
    }
  };

  // Текст для роли
  const getRoleText = (role) => {
    switch (role) {
      case 'admin': return 'Админ';
      case 'moderator': return 'Модератор';
      case 'superuser': return 'Суперпользователь';
      default: return 'Пользователь';
    }
  };

  // Вариант для статуса
  const getStatusVariant = (isActive) => {
    return isActive ? 'success' : 'secondary';
  };

  // Форматирование даты
  const formatDate = (dateString) => {
    if (!dateString) return 'Не указано';
    try {
      return new Date(dateString).toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return 'Неверная дата';
    }
  };

  // Сброс фильтров
  const resetFilters = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  // Рендер пагинации
  const renderPagination = () => {
    if (totalPages <= 1 && totalUsers <= usersPerPage) {
      return (
        <div className="text-center text-muted py-2">
          <small>
            Показаны все <strong>{totalUsers}</strong> пользователей
            {searchTerm.trim() && ' по вашему запросу'}
          </small>
        </div>
      );
    }

    const startUser = ((currentPage - 1) * usersPerPage) + 1;
    const endUser = Math.min(currentPage * usersPerPage, totalUsers);

    return (
      <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-3">
          <div>
            <span className="text-muted">
              Показано <strong>{startUser}</strong>-<strong>{endUser}</strong> из <strong>{totalUsers}</strong> пользователей
              {searchTerm.trim() && ' по вашему запросу'}
            </span>
          </div>
          
          <div className="d-flex align-items-center">
            <span className="text-muted me-2">На странице:</span>
            <DropdownButton
              id="dropdown-users-per-page"
              title={usersPerPage}
              variant="outline-secondary"
              size="sm"
            >
              {[10, 20, 50, 100].map(option => (
                <Dropdown.Item 
                  key={option}
                  active={usersPerPage === option}
                  onClick={() => {
                    setUsersPerPage(option);
                    setCurrentPage(1);
                  }}
                >
                  {option} пользователей
                </Dropdown.Item>
              ))}
            </DropdownButton>
          </div>
        </div>
        
        <Pagination className="mb-0">
          <Pagination.Prev 
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            title="Предыдущая страница"
          />
          
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let page;
            if (totalPages <= 5) {
              page = i + 1;
            } else if (currentPage <= 3) {
              page = i + 1;
            } else if (currentPage >= totalPages - 2) {
              page = totalPages - 4 + i;
            } else {
              page = currentPage - 2 + i;
            }
            
            return (
              <Pagination.Item
                key={page}
                active={page === currentPage}
                onClick={() => setCurrentPage(page)}
                title={`Страница ${page}`}
              >
                {page}
              </Pagination.Item>
            );
          })}
          
          <Pagination.Next 
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            title="Следующая страница"
          />
        </Pagination>
        
        <div className="text-muted">
          <small>Страница <strong>{currentPage}</strong> из <strong>{totalPages}</strong></small>
        </div>
      </div>
    );
  };

  if (loading && users.length === 0) {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body className="text-center py-5">
          <Spinner animation="border" variant="primary" className="mb-3" />
          <p className="text-muted">Загрузка пользователей...</p>
          <small className="text-muted">Пожалуйста, подождите</small>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div className="users-tab">
      <Card className="mb-3 border-0 shadow-sm">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-0">
              <i className="bi bi-people me-2"></i>
              Управление пользователями
            </h5>
            {!loading && (
              <small className="text-muted">
                Всего пользователей в системе: <strong>{totalUsers}</strong>
              </small>
            )}
          </div>
          <div className="d-flex gap-2">
            <InputGroup style={{ width: '300px' }}>
              <Form.Control
                placeholder="Поиск по имени, email или username..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={searchLoading}
              />
              <Button 
                variant="outline-secondary"
                onClick={handleSearch}
                disabled={searchLoading}
              >
                {searchLoading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <i className="bi bi-search"></i>
                )}
              </Button>
              {searchTerm && (
                <Button 
                  variant="outline-secondary" 
                  onClick={resetFilters}
                  title="Очистить поиск"
                  disabled={searchLoading}
                >
                  <i className="bi bi-x"></i>
                </Button>
              )}
            </InputGroup>
            <div className="btn-group">
              <Button 
                variant="outline-primary" 
                onClick={loadUsers}
                title="Обновить список"
                disabled={loading || searchLoading}
              >
                {loading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <i className="bi bi-arrow-clockwise"></i>
                )}
              </Button>
              <Button 
                variant="outline-secondary"
                onClick={resetFilters}
                title="Сбросить фильтры"
                disabled={loading || searchLoading}
              >
                <i className="bi bi-arrow-counterclockwise"></i>
              </Button>
            </div>
          </div>
        </Card.Header>
      </Card>

      {error && (
        <Alert variant="danger" className="mb-3" dismissible onClose={() => setError('')}>
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      )}

      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th width="50"></th>
                  <th>Пользователь</th>
                  <th>Контакты</th>
                  <th>Роль</th>
                  <th>Статус</th>
                  {/* <th>Дата регистрации</th> */}
                  <th width="120">Действия</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-4">
                      <i className="bi bi-people display-4 text-muted mb-3 opacity-50"></i>
                      <h5 className="text-muted">
                        {searchTerm ? 'Пользователи не найдены' : 'Пользователей нет'}
                      </h5>
                      <p className="text-muted mb-0">
                        {searchTerm ? 'Попробуйте изменить поисковый запрос' : 'В системе пока нет зарегистрированных пользователей'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  users.map(user => (
                    <React.Fragment key={user.id}>
                      {/* Основная строка */}
                      <tr 
                        onClick={() => toggleExpand(user.id)}
                        className="cursor-pointer"
                        style={{ cursor: 'pointer' }}
                      >
                        <td>
                          <i 
                            className={`bi bi-chevron-${expandedUser === user.id ? 'down' : 'right'} text-muted`}
                          ></i>
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <div 
                              className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-3 text-white fw-bold"
                              style={{ width: '40px', height: '40px', fontSize: '0.9rem' }}
                            >
                              {getInitials(user)}
                            </div>
                            <div>
                              <div className="fw-semibold">{getDisplayName(user)}</div>
                              <div className="text-muted small">
                                @{getUsername(user)} • ID: {user.id}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div>
                            <div className="fw-semibold">{user.email || 'Email не указан'}</div>
                            {user.telegram_username && (
                              <div className="text-muted small">
                                <i className="bi bi-telegram me-1"></i>
                                @{user.telegram_username}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <Badge bg={getRoleBadgeVariant(user.role)}>
                            {getRoleText(user.role)}
                            {user.is_superuser && ' ★'}
                          </Badge>
                        </td>
                        <td>
                          <Button
                            variant={getStatusVariant(user.is_active)}
                            size="sm"
                            onClick={(e) => handleToggleActive(user.id, user.is_active, e)}
                            className="w-100"
                          >
                            {user.is_active ? 'Активен' : 'Неактивен'}
                          </Button>
                        </td>
                        {/* <td>
                          <small className="text-muted">
                             {formatDate(user.created_at)} 
                          </small>
                        </td> */}
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={(e) => startEdit(user, e)}
                              title="Редактировать"
                            >
                              <i className="bi bi-pencil"></i>
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={(e) => handleDeleteClick(user, e)}
                              title="Удалить"
                            >
                              <i className="bi bi-trash"></i>
                            </Button>
                          </div>
                        </td>
                      </tr>

                      {/* Расширенная информация */}
                      <tr>
                        <td colSpan={7} className="p-0 border-0">
                          <Collapse in={expandedUser === user.id}>
                            <div className="bg-light">
                              <div className="p-3">
                                {editingUser?.id === user.id ? (
                                  // Режим редактирования
                                  <div className="bg-white rounded p-3 border">
                                    <h6 className="mb-3">
                                      <i className="bi bi-pencil-square me-2 text-primary"></i>
                                      Редактирование пользователя
                                    </h6>
                                    <Row className="g-3">
                                      <Col md={6}>
                                        <Form.Group>
                                          <Form.Label>Полное имя</Form.Label>
                                          <Form.Control
                                            type="text"
                                            value={editingUser.name || ''}
                                            onChange={(e) => handleInputChange('name', e.target.value)}
                                            placeholder="Введите имя"
                                          />
                                        </Form.Group>
                                      </Col>
                                      <Col md={6}>
                                        <Form.Group>
                                          <Form.Label>Email</Form.Label>
                                          <Form.Control
                                            type="email"
                                            value={editingUser.email || ''}
                                            onChange={(e) => handleInputChange('email', e.target.value)}
                                            placeholder="Введите email"
                                          />
                                        </Form.Group>
                                      </Col>
                                      <Col md={6}>
                                        <Form.Group>
                                          <Form.Label>Роль</Form.Label>
                                          <Form.Select
                                            value={editingUser.role || 'user'}
                                            onChange={(e) => handleInputChange('role', e.target.value)}
                                          >
                                            <option value="user">Пользователь</option>
                                            <option value="moderator">Модератор</option>
                                            <option value="admin">Администратор</option>
                                          </Form.Select>
                                        </Form.Group>
                                      </Col>
                                      <Col md={6}>
                                        <Form.Group>
                                          <Form.Label>Статус</Form.Label>
                                          <Form.Select
                                            value={editingUser.is_active ? 'active' : 'inactive'}
                                            onChange={(e) => handleInputChange('is_active', e.target.value === 'active')}
                                          >
                                            <option value="active">Активен</option>
                                            <option value="inactive">Неактивен</option>
                                          </Form.Select>
                                        </Form.Group>
                                      </Col>
                                      <Col xs={12}>
                                        <div className="d-flex gap-2 justify-content-end border-top pt-3">
                                          <Button
                                            variant="success"
                                            onClick={handleSave}
                                            disabled={saving}
                                          >
                                            {saving ? (
                                              <>
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Сохранение...
                                              </>
                                            ) : (
                                              <>
                                                <i className="bi bi-check-lg me-2"></i>
                                                Сохранить
                                              </>
                                            )}
                                          </Button>
                                          <Button
                                            variant="secondary"
                                            onClick={cancelEdit}
                                          >
                                            <i className="bi bi-x-lg me-2"></i>
                                            Отмена
                                          </Button>
                                        </div>
                                      </Col>
                                    </Row>
                                  </div>
                                ) : (
                                  // Режим просмотра
                                  <Row className="g-4">
                                    <Col md={6}>
                                      <Card className="h-100">
                                        <Card.Header className="bg-white">
                                          <h6 className="mb-0">
                                            <i className="bi bi-info-circle me-2"></i>
                                            Основная информация
                                          </h6>
                                        </Card.Header>
                                        <Card.Body>
                                          <div className="mb-2">
                                            <small className="text-muted">ID пользователя</small>
                                            <div className="fw-semibold">#{user.id}</div>
                                          </div>
                                          <div className="mb-2">
                                            <small className="text-muted">Отображаемое имя</small>
                                            <div className="fw-semibold">{getDisplayName(user)}</div>
                                          </div>
                                          <div className="mb-2">
                                            <small className="text-muted">Системное имя</small>
                                            <div className="fw-semibold">{user.name || 'Не указано'}</div>
                                          </div>
                                          {user.telegram_first_name && (
                                            <div className="mb-2">
                                              <small className="text-muted">Имя в Telegram</small>
                                              <div className="fw-semibold">{user.telegram_first_name}</div>
                                            </div>
                                          )}
                                          {user.telegram_last_name && (
                                            <div className="mb-2">
                                              <small className="text-muted">Фамилия в Telegram</small>
                                              <div className="fw-semibold">{user.telegram_last_name}</div>
                                            </div>
                                          )}
                                          {user.telegram_id && (
                                            <div className="mb-2">
                                              <small className="text-muted">Telegram ID</small>
                                              <div className="fw-semibold">{user.telegram_id}</div>
                                            </div>
                                          )}
                                          {user.is_superuser && (
                                            <div className="mb-2">
                                              <Badge bg="warning" className="w-100">
                                                <i className="bi bi-star-fill me-1"></i>
                                                Суперпользователь
                                              </Badge>
                                            </div>
                                          )}
                                        </Card.Body>
                                      </Card>
                                    </Col>
                                    <Col md={6}>
                                      <Card className="h-100">
                                        <Card.Header className="bg-white">
                                          <h6 className="mb-0">
                                            <i className="bi bi-telephone me-2"></i>
                                            Контактная информация
                                          </h6>
                                        </Card.Header>
                                        <Card.Body>
                                          <div className="mb-2">
                                            <small className="text-muted">Email</small>
                                            <div className="fw-semibold">{user.email || 'Не указан'}</div>
                                            {user.email_verified && (
                                              <Badge bg="success" className="ms-2">
                                                <i className="bi bi-check-lg me-1"></i>
                                                Подтвержден
                                              </Badge>
                                            )}
                                          </div>
                                          {user.telegram_username && (
                                            <div className="mb-2">
                                              <small className="text-muted">Telegram</small>
                                              <div className="fw-semibold">
                                                <i className="bi bi-telegram me-1 text-primary"></i>
                                                @{user.telegram_username}
                                              </div>
                                            </div>
                                          )}
                                          {user.telegram_id ? (
                                            <Badge bg="success" className="w-100">
                                              <i className="bi bi-check-circle me-1"></i>
                                              Telegram привязан
                                            </Badge>
                                          ) : (
                                            <Badge bg="secondary" className="w-100">
                                              <i className="bi bi-x-circle me-1"></i>
                                              Telegram не привязан
                                            </Badge>
                                          )}
                                        </Card.Body>
                                      </Card>
                                    </Col>
                                  </Row>
                                )}
                              </div>
                            </div>
                          </Collapse>
                        </td>
                      </tr>
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          <div className="p-3 border-top">
            {renderPagination()}
          </div>
        </Card.Body>
      </Card>

      {/* Модальное окно подтверждения удаления */}
      <Modal show={!!deleteConfirm} onHide={handleDeleteCancel} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <i className="bi bi-exclamation-triangle me-2"></i>
            Подтверждение удаления
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Вы точно хотите удалить пользователя <strong>"{getDisplayName(deleteConfirm)}"</strong>?
          </p>
          <Alert variant="danger" className="mb-3">
            <i className="bi bi-exclamation-triangle me-2"></i>
            Это действие невозможно отменить. Все данные пользователя будут безвозвратно удалены.
          </Alert>
          <div className="bg-light p-3 rounded">
            <p className="mb-1"><strong>ID:</strong> {deleteConfirm?.id}</p>
            <p className="mb-1"><strong>Email:</strong> {deleteConfirm?.email || 'Не указан'}</p>
            <p className="mb-1"><strong>Роль:</strong> {getRoleText(deleteConfirm?.role)}</p>
            <p className="mb-0"><strong>Telegram:</strong> {deleteConfirm?.telegram_username ? `@${deleteConfirm.telegram_username}` : 'Не привязан'}</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleDeleteCancel} disabled={deleting}>
            Отмена
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm} disabled={deleting}>
            {deleting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Удаление...
              </>
            ) : (
              'Удалить'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UsersTab;